import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { passwordSchema, type User } from '@afiliados/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { usersApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { PasswordInput } from './PasswordInput';

export function ResetPasswordDialog({
  user,
  onOpenChange,
}: {
  user: User | null;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [mustChange, setMustChange] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setPassword('');
      setMustChange(true);
      setError(null);
    }
  }, [user]);

  const mut = useMutation({
    mutationFn: () =>
      usersApi.resetPassword(user!.id, { password, mustChangePassword: mustChange }),
    onSuccess: () => {
      toast.success('Senha redefinida. As sessões do usuário foram encerradas.');
      void qc.invalidateQueries({ queryKey: ['users'] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Erro ao redefinir'),
  });

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Redefinir senha</DialogTitle>
          <DialogDescription>
            {user ? `${user.name} (${user.email})` : ''}. Todas as sessões do usuário serão
            encerradas.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            const check = passwordSchema.safeParse(password);
            if (!check.success) {
              setError(check.error.issues[0]?.message ?? 'Senha inválida');
              return;
            }
            mut.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="reset-password">Nova senha temporária</Label>
            <PasswordInput id="reset-password" value={password} onChange={setPassword} />
            {error ? (
              <p className="text-xs text-destructive">{error}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Pelo menos 8 caracteres, com letra e número.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Switch id="reset-must-change" checked={mustChange} onCheckedChange={setMustChange} />
            <Label htmlFor="reset-must-change">Exigir troca de senha no próximo acesso</Label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending && <Loader2 className="animate-spin" />} Redefinir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
